import { MatchRequirementStub, SandboxForcedStatus } from 'dashboard/models/simulate/stubs';
import { WorkflowRunAttributes } from 'dashboard/models/workflow-run';
import { WorkflowStepActionKey } from 'dashboard/models/workflow-version';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { CheckName } from 'lib/verificationCheck';

interface SimulateTriggerBase {
  overrides?: Record<string, any> | null;
}

export interface TxnSimulateTriggerObject extends SimulateTriggerBase {
  type: 'transaction';
  'txn-type-id'?: string | null;
  'txn-status-id'?: string | null;
}

export interface InquiryTriggerObject extends SimulateTriggerBase {
  type: 'inquiry';
  'inquiry-template-id'?: string | null;
}

export interface ReportTriggerObject extends SimulateTriggerBase {
  type: 'report';
  'report-template-id'?: string | null;
}

export interface CaseTriggerObject extends SimulateTriggerBase {
  type: 'case';
  'case-template-id'?: string | null;
  'case-state-id'?: string | null;
}

export interface ExistingPersonaObject extends SimulateTriggerBase {
  type: 'object';
  'persona-object-id'?: string | null;
}

export type SimulateTrigger =
  | TxnSimulateTriggerObject
  | InquiryTriggerObject
  | CaseTriggerObject
  | ExistingPersonaObject
  // TODO: ReportTriggerObject is not yet fully implemented
  | ReportTriggerObject;

export interface BaseStepOverride {
  output?: Record<string, string>;
}

export interface StepOverrideRunVerification extends BaseStepOverride {
  'check-stubs'?: RunVerificationCheckStub[];
  'status-stub'?: SandboxForcedStatus;
}

interface StepOverrideRunReport extends BaseStepOverride {
  'status-stub'?: ReportForcedStatus;
}

export interface StepOverrideListMatch {
  output?: {
    matched: boolean;
  };
}

export interface StepOverrideEvaluateCode {
  output?: {
    result: string | number | boolean | object;
  };
}

export type StepOverride =
  | BaseStepOverride
  | StepOverrideRunVerification
  | StepOverrideListMatch
  | StepOverrideRunReport
  | StepOverrideEvaluateCode;

export interface WorkflowRunSimulateContext {
  trigger?: SimulateTrigger;
  overrides?: SimulateOverrides;
  'initial-step-token'?: string;
  'terminal-step-token'?: string;
}

export interface SimulateOverrides {
  steps?: Record<string, StepOverride>;
  'method-chains'?: Record<string, string>;
}

export interface WorkflowRunSimulatedAttributes extends WorkflowRunAttributes {
  'simulate-context'?: WorkflowRunSimulateContext;
  'started-at': string;
  'failure-reason': string | null;
}
export interface WorkflowRunSimulatedRelationships {
  workflow: ApiRelationship<ApiModel.Workflow>;
  'workflow-version': ApiRelationship<ApiModel.WorkflowVersion>;
  'trigger-object': ApiRelationship<ApiModel.Transaction>;
  steps: ApiListRelationship<ApiModel.WorkflowRunStep>;
  creator: ApiRelationship<ApiModel.User> | ApiRelationship<ApiModel.Event>;
  environment: ApiRelationship<ApiModel.EnvironmentSandbox>;
  'child-workflow-runs': ApiListRelationship<ApiModel.WorkflowRunSimulated>;
}

export interface RunVerificationCheckStub {
  name: CheckName | undefined;
  reasons?: string[];
  matchRequirementStubs?: MatchRequirementStub[];
}

export type WorkflowRunSimulated = ApiResource<
  WorkflowRunSimulatedAttributes,
  WorkflowRunSimulatedRelationships,
  ApiModel.WorkflowRunSimulated
>;

export const SIMULTATE_OVERRIDABLE_ACTION_TYPES = [
  WorkflowStepActionKey.ListMatch,
  WorkflowStepActionKey.RunReportGeneric,
  WorkflowStepActionKey.RunVerificationGeneric,
  WorkflowStepActionKey.EvaluateCode,
] as const;

export type OverridableWorkflowStepActionKey = (typeof SIMULTATE_OVERRIDABLE_ACTION_TYPES)[number];

export enum ReportForcedStatus {
  Matched = 'matched',
  NotMatched = 'not-matched',
}

export const isStepOverrideRunVerificationType = (
  step: StepOverride,
): step is StepOverrideRunVerification => {
  return 'check-stubs' in step;
};

export const OVERRIDABLE_REPORT_TYPES = [
  WorkflowStepActionKey.RunReportWatchlist,
  WorkflowStepActionKey.RunReportBusinessAdverseMedia,
  WorkflowStepActionKey.RunReportBusinessWatchlist,
  WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
  WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
  WorkflowStepActionKey.RunReportAdverseMedia,
  WorkflowStepActionKey.RunReportScreening,
];
